import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { company } from "@/mocks/home";
import { legalUpdated } from "@/mocks/legal";

interface LegalSection {
  heading: string;
  paragraphs: string[];
}

interface LegalData {
  title: string;
  intro: string;
  sections: LegalSection[];
}

interface LegalPageProps {
  data: LegalData;
}

const legalLinks = [
  { label: "Política de Privacidade", to: "/politica-de-privacidade" },
  { label: "Termos e Condições", to: "/termos-e-condicoes" },
  { label: "Política de Cookies", to: "/politica-de-cookies" },
];

export default function LegalPage({ data }: LegalPageProps) {
  const slugify = (heading: string) =>
    heading
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");

  return (
    <div className="min-h-screen w-full bg-background-50">
      <header className="sticky top-0 z-40 w-full border-b border-background-200/70 bg-background-50/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-container items-center justify-between px-4 md:h-20 md:px-6 lg:px-10">
          <Link to="/" className="flex cursor-pointer items-center gap-2.5" aria-label="Porta31 Condomínios - início">
            <img
              src={company.logo}
              alt="Logótipo da Porta31 Condomínios"
              title="Porta31 Condomínios"
              className="h-14 w-auto object-contain md:h-16"
            />
            <span className="font-heading text-base font-bold leading-tight tracking-tight text-foreground-950 md:text-lg">
              {company.name}
            </span>
          </Link>
          <Link
            to="/"
            className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors duration-300 hover:border-primary-400 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar ao início
          </Link>
        </div>
      </header>

      <section className="w-full border-b border-background-200/70 bg-background-100/60">
        <div className="mx-auto w-full max-w-container px-4 py-12 md:px-6 md:py-16 lg:px-10">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-primary-700">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-500/10">
              <FileText className="h-4 w-4 text-primary-600" />
            </span>
            Informação Legal
          </div>
          <h1 className="mt-5 font-heading text-3xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
            {data.title}
          </h1>
          <p className="mt-2 text-xs font-medium uppercase tracking-[0.14em] text-foreground-500">
            Última atualização: {legalUpdated}
          </p>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-foreground-600 md:text-lg">{data.intro}</p>
        </div>
      </section>

      <main className="mx-auto w-full max-w-container px-4 py-12 md:px-6 md:py-16 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-[260px_1fr]">
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-foreground-500">Nesta página</p>
            <nav className="mt-4 flex flex-col gap-1.5" aria-label="Índice da página">
              {data.sections.map((section) => (
                <a
                  key={section.heading}
                  href={`#${slugify(section.heading)}`}
                  className="cursor-pointer rounded-md border-l-2 border-transparent py-1.5 pl-3 text-sm text-foreground-600 transition-colors duration-300 hover:border-primary-400 hover:text-primary-700"
                >
                  {section.heading}
                </a>
              ))}
            </nav>
          </aside>

          <article className="max-w-3xl">
            {data.sections.map((section) => (
              <section
                key={section.heading}
                id={slugify(section.heading)}
                className="scroll-mt-28 border-t border-background-200/70 py-7 first:border-t-0 first:pt-0"
              >
                <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">{section.heading}</h2>
                <div className="mt-3 space-y-3">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph} className="text-sm leading-relaxed text-foreground-600 md:text-base">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
              <p className="text-sm leading-relaxed text-foreground-700">
                Tem alguma dúvida sobre este documento? Contacte-nos através de{" "}
                <a
                  href={`mailto:${company.email}`}
                  className="cursor-pointer font-medium text-primary-700 underline-offset-2 hover:underline"
                >
                  {company.email}
                </a>{" "}
                ou pelo telefone{" "}
                <a
                  href={company.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cursor-pointer font-medium text-primary-700 underline-offset-2 hover:underline"
                >
                  {company.phoneDisplay}
                </a>
                .
              </p>
            </div>
          </article>
        </div>
      </main>

      <footer className="w-full bg-secondary-950">
        <div className="mx-auto w-full max-w-container px-4 py-12 md:px-6 lg:px-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-sm">
              <div className="flex items-center gap-3">
                <img
                  src={company.logo}
                  alt="Logótipo da Porta31 Condomínios"
                  title="Porta31 Condomínios"
                  className="h-14 w-auto object-contain md:h-16"
                />
                <span className="font-heading text-lg font-bold leading-tight tracking-tight text-background-50">
                  {company.name}
                </span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-background-300">
                {company.slogan} Gestão de condomínios em Vila Nova de Famalicão.
              </p>
            </div>

            <div>
              <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-background-50">Informação Legal</h3>
              <ul className="mt-4 space-y-2.5">
                {legalLinks.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="cursor-pointer text-sm text-background-300 transition-colors duration-300 hover:text-primary-300"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 flex flex-col items-center justify-between gap-3 border-t border-background-50/10 pt-6 sm:flex-row">
            <p className="text-xs text-background-400">
              © {new Date().getFullYear()} {company.name}. Todos os direitos reservados.
            </p>
            <p className="text-xs text-background-400">
              Website desenvolvido por{" "}
              <a
                href="https://ponto-zero.pages.dev/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Ponto Zero Apps"
                className="cursor-pointer font-medium text-background-200 transition-colors duration-300 hover:text-primary-300"
              >
                Ponto Zero Apps
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}